<template>
  <div class="shop-edit">
    <div class="back-btn" @click="goBack">← 返回列表</div>

    <div class="edit-header">
      <h2>修改店家资料</h2>
    </div>

    <form @submit.prevent="updateShop" class="edit-form">
      <div class="form-item">
        <label>店家名称 *</label>
        <input 
          type="text" 
          v-model="shopData.name" 
          placeholder="请输入店家名称"
          class="form-input"
          required
        />
      </div>
      <div class="form-item">
        <label>归属于用户</label>
        <div class="search-select-container">
          <input 
            type="text" 
            v-model="userSearchKey" 
            placeholder="搜索用户..."
            class="search-select-input"
            @click.stop="showUserDropdown = true"
          />
          <div class="search-select-arrow" @click="showUserDropdown = !showUserDropdown">▼</div>
          <div v-if="showUserDropdown" class="search-select-dropdown">
            <div 
              v-for="user in filteredUsers" 
              :key="user.id"
              :class="'dropdown-item' + (shopData.userId === user.id.toString() ? ' selected' : '')"
              @click="selectUser(user)"
            >
              {{ user.name }} ({{ user.phone }})
            </div>
            <div v-if="filteredUsers.length === 0" class="dropdown-empty">
              未找到匹配的用户
            </div>
          </div>
        </div>
        <div v-if="shopData.userId" class="selected-user">
          已选择: {{ getSelectedUserName() }}
        </div>
      </div>
      <div class="form-item">
        <label>联系人 *</label>
        <input 
          type="text" 
          v-model="shopData.contactName" 
          placeholder="请输入联系人姓名"
          class="form-input"
          required
        />
      </div>
      <div class="form-item">
        <label>手机号 *</label>
        <input 
          type="tel" 
          v-model="shopData.phone" 
          placeholder="请输入手机号"
          class="form-input"
          required
        />
      </div>
      <div class="form-item">
        <label>所属小区 *</label>
        <select v-model="shopData.community" class="form-select" required>
          <option value="">请选择小区</option>
          <option v-for="community in communities" :key="community.id" :value="community.name">
            {{ community.name }}
          </option>
        </select>
      </div>
      <div class="form-item">
        <label>详细地址 *</label>
        <input 
          type="text" 
          v-model="shopData.address" 
          placeholder="请输入楼栋单元门牌号等"
          class="form-input"
          required
        />
      </div>
      <div class="form-footer">
        <button type="button" @click="goBack" class="btn-cancel">取消</button>
        <button type="submit" class="btn-submit">保存修改</button>
      </div>
    </form>
  </div>
</template>

<script>
import { getCommunityList } from '@/api/communities'
import { getShopDetail, updateShop } from '@/api/shops'
import { getUserList } from '@/api/users'

export default {
  name: 'ShopEdit',
  computed: {
    filteredUsers() {
      if (!this.userSearchKey) {
        return this.users
      }
      const key = this.userSearchKey.toLowerCase()
      return this.users.filter(user => 
        user.name.toLowerCase().includes(key) || 
        user.phone.includes(key)
      )
    }
  },
  data() {
    return {
      shopData: {
        id: null,
        name: '',
        userId: '',
        contactName: '',
        phone: '',
        community: '',
        address: ''
      },
      users: [],
      communities: [],
      userSearchKey: '',
      showUserDropdown: false
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    this.loadShopData()
    this.loadCommunities()
    this.loadUsers()
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    async loadUsers() {
      try {
        const response = await getUserList()
        if (response && response.data) {
          const pageData = response.data
          const usersData = pageData.data || pageData.list || []
          
          this.users = usersData.map(user => ({
            id: user.id,
            name: user.name || user.nickname || user.username || '',
            phone: user.phone || ''
          }))
        }
      } catch (error) {
      }
    },
    async loadShopData() {
      try {
        const id = this.$route.params.id
        const response = await getShopDetail(id)
        
        if (response && response.data) {
          const data = response.data
          this.shopData = {
            id: data.id,
            name: data.name,
            userId: data.user_id ? data.user_id.toString() : '',
            contactName: data.contact_name,
            phone: data.phone,
            community: data.community?.name || '',
            address: data.address || ''
          }
        }
      } catch (error) {
        alert('加载店家数据失败，请稍后重试')
      }
    },
    async loadCommunities() {
      try {
        const response = await getCommunityList()
        if (response && response.data) {
          let communitiesData = response.data.list || response.data.data || response.data
          if (!Array.isArray(communitiesData)) {
            if (communitiesData.data && Array.isArray(communitiesData.data)) {
              communitiesData = communitiesData.data
            } else {
              communitiesData = []
            }
          }
          this.communities = communitiesData.map(community => ({
            id: community.id,
            name: community.name
          }))
        }
      } catch (error) {
      }
    },
    handleClickOutside(e) {
      if (this.showUserDropdown && !e.target.closest('.search-select-container')) {
        this.showUserDropdown = false
      }
    },
    selectUser(user) {
      this.shopData.userId = user.id.toString()
      this.userSearchKey = ''
      this.showUserDropdown = false
    },
    getSelectedUserName() {
      const user = this.users.find(u => u.id.toString() === this.shopData.userId)
      return user ? `${user.name} (${user.phone})` : ''
    },
    goBack() {
      this.$router.push('/user/shops')
    },
    async updateShop() {
      try {
        const selectedCommunity = this.communities.find(c => c.name === this.shopData.community)
        
        const updateData = {
          name: this.shopData.name,
          user_id: this.shopData.userId ? parseInt(this.shopData.userId) : null,
          contact_name: this.shopData.contactName,
          phone: this.shopData.phone,
          community_id: selectedCommunity ? selectedCommunity.id : null,
          address: this.shopData.address
        }

        await updateShop(this.shopData.id, updateData)
        
        alert('店家资料修改成功！')
        this.$router.push('/user/shops')
      } catch (error) {
        alert('修改店家资料失败，请稍后重试')
      }
    }
  }
}
</script>

<style scoped>
.shop-edit {
  padding: 20px;
}

.back-btn {
  color: #3498db;
  cursor: pointer;
  margin-bottom: 20px;
  font-size: 14px;
}

.edit-header {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 20px;
}

.edit-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.edit-form {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
}

.form-item {
  margin-bottom: 16px;
}

.form-item label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #333;
}

.form-input, .form-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-input:focus, .form-select:focus {
  outline: none;
  border-color: #3498db;
}

.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.btn-cancel {
  padding: 10px 20px;
  background: #f5f5f5;
  color: #666;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.btn-submit {
  padding: 10px 20px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.search-select-container {
  position: relative;
  width: 100%;
}

.search-select-input {
  width: 100%;
  padding: 10px 12px;
  padding-right: 30px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.search-select-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 12px;
  color: #999;
}

.search-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 100;
}

.dropdown-item {
  padding: 10px 12px;
  cursor: pointer;
  font-size: 14px;
}

.dropdown-item:hover {
  background: #f5f5f5;
}

.dropdown-item.selected {
  background: #e6f7ff;
  color: #1890ff;
}

.dropdown-empty {
  padding: 10px 12px;
  color: #999;
  font-size: 14px;
}

.selected-user {
  margin-top: 8px;
  font-size: 13px;
  color: #1890ff;
}
</style>